import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useParams, useNavigate } from 'react-router-dom';
import { Descriptions, Card, Spin, Button, Space, Tag, Empty, Row, Col, message } from 'antd';
import { ArrowLeftOutlined, MailOutlined, PhoneOutlined, GlobalOutlined, CalendarOutlined, UserOutlined, HomeOutlined, TeamOutlined, DollarOutlined, FilePdfOutlined } from '@ant-design/icons';
import { WebApiMemberDtoMemberDto, WebApiModelsMemberField, WebApiModelsMembershipProduct, WebApiServicesDataTransferObjectsGroupServiceGroupResultDto } from '../../Repository/eventbookyapi';
import { MemberService } from '../../Services/MemberService';
import { MemberFieldService } from '../../Services/MemberFieldService';
import { MembershipProductService } from '../../Services/MembershipProductService';
import { IntegrationService } from '../../Services/IntegrationService';
import { GroupService } from '../../Services/GroupService';
import { TextService } from '../../Services/TextService';
import { useAuth } from '../../Contexts/AuthContext';
import LayoutLeftMenu from '../../Components/Layout/LayoutLeftMenu';
import LeftMenu from './LeftMenu';
import { formatDateString, getCurrentYearAsString, isValidMemberDate } from '../../Helpers/DateHelpers';
import { getCurrentLanguage } from '../../Helpers/RouteHelper';
import MemberEditModal from '../../Components/Users/MemberEditModal';
import ManagePayments from '../../Components/Users/ManagePayments/ManagePayments';
import { MemberStatusType } from '../../Types/MemberStatusType';
import { UiControlType } from '../../Types/UiControlType';
import { CompanyTypeConfiguration } from '../../Types/CompanyTypes';
import { IntegrationType } from '../../Types/IntegrationType';
import { PaymentConfiguration } from '../../Types/PaymentConfiguration';
import MemberCustomListConnections from '../../Components/Users/MemberCustomListConnections';
import MemberPaymentDocuments from '../../Components/Users/MemberPaymentDocuments';
import { downloadBlobFile, getFileNameFromContentDisposition } from '../../Helpers/FileDownloadHelper';

const MemberDetails = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const GetText = TextService.GetText;

    const [member, setMember] = useState<WebApiMemberDtoMemberDto | null>(null);
    const [memberFieldSettings, setMemberFieldSettings] = useState<WebApiModelsMemberField[]>([]);
    const [membershipProducts, setMembershipProducts] = useState<WebApiModelsMembershipProduct[]>([]);
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [hasFortnoxIntegration, setHasFortnoxIntegration] = useState(false);
    const [hasPingPaymentIntegration, setHasPingPaymentIntegration] = useState(false);
    const [hasSpirisIntegration, setHasSpirisIntegration] = useState(false);
    const [groups, setGroups] = useState<WebApiServicesDataTransferObjectsGroupServiceGroupResultDto[]>([]);
    const [downloadingPaymentDocument, setDownloadingPaymentDocument] = useState(false);

    const handleBack = () => {
        // Check if there's history to go back to within the app
        if (window.history.length > 1) {
            navigate(-1);
        } else {
            // Fallback to users list if no history
            navigate(`/${getCurrentLanguage()}/users`);
        }
    };

    useEffect(() => {
        loadMemberDetails();
    }, [id]);

    const loadMemberDetails = async () => {
        try {
            setLoading(true);
            const [memberData, fields, products, integrations, groupsData] = await Promise.all([
                MemberService.GetAllMembers(), // We'll filter by id
                MemberFieldService.GetMemberFields(),
                MembershipProductService.GetAllMembershipProducts(),
                IntegrationService.GetActiveIntegrations(),
                GroupService.GetGroups()
            ]);

            setMemberFieldSettings(fields);
            setMembershipProducts(products);
            setGroups(groupsData);

            // Check if Fortnox integration exists
            const hasFortnox = integrations.some(i => i.type === IntegrationType.Fortnox);
            setHasFortnoxIntegration(hasFortnox);
            const hasPing = integrations.some(i => i.type === IntegrationType.PingPayments);
            setHasPingPaymentIntegration(hasPing);

            const hasSpiris = integrations.some(i => i.type === IntegrationType.Spiris);
            setHasSpirisIntegration(hasSpiris);

            // Find the specific member by memberId
            const foundMember = memberData.members?.find(m => m.memberId === Number(id));
            if (foundMember) {
                setMember(foundMember);
            } else {
                // Member not found
                setMember(null);
            }
        } catch (error) {
            console.error('Error loading member details:', error);
        } finally {
            setLoading(false);
        }
    };

    const getMembershipProductName = (productId?: number) => {
        if (!productId) return '-';
        const product = membershipProducts.find(p => p.id === productId);
        return product?.name || '-';
    };

    const getMembershipProductNames = (productIds?: number[] | null) => {
        if (!productIds || productIds.length === 0) return '-';
        const names = productIds
            .map(id => membershipProducts.find(p => p.id === id)?.name)
            .filter(Boolean);
        return names.length > 0 ? names.join(', ') : '-';
    };

    const getStatusTag = (status?: MemberStatusType) => {
        switch (status) {
            case MemberStatusType.Active:
                return <Tag color="green">{GetText("MemberList_StatusActive")}</Tag>;
            case MemberStatusType.Cancelled:
                return <Tag color="red">{GetText("MemberList_StatusCancelled")}</Tag>;
            case MemberStatusType.UnconfirmedEmail:
                return <Tag color="orange">{GetText("MemberList_StatusUnconfirmedEmail")}</Tag>;
            case MemberStatusType.UnconfirmedInitialPayment:
                return <Tag color="gold">{GetText("MemberList_StatusUnconfirmedInitialPayment")}</Tag>;
            default:
                return <Tag>{GetText("MemberList_StatusActive")}</Tag>;
        }
    };

    const getPaymentStatusTag = (isPaid?: boolean) => {
        return isPaid
            ? <Tag color="green">{GetText("MemberList_Paid")}</Tag>
            : <Tag color="red">{GetText("MemberList_Unpaid")}</Tag>;
    };

    const downloadInvoiceFromPaymentLink = async (paymentLinkId: string) => {
        try {
            setDownloadingPaymentDocument(true);
            const result = await IntegrationService.DownloadInvoiceFromPaymentLink(paymentLinkId);
            const fileName = getFileNameFromContentDisposition(result.contentDisposition, `invoice-${paymentLinkId}.pdf`);
            downloadBlobFile(result.blob, fileName);
        } catch {
            message.error(GetText("AddPaymentLink_InvoiceCreationError"));
        } finally {
            setDownloadingPaymentDocument(false);
        }
    };

    const downloadReceiptFromPaymentLink = async (paymentLinkId: string) => {
        try {
            setDownloadingPaymentDocument(true);
            const result = await IntegrationService.DownloadReceiptFromPaymentLink(paymentLinkId);
            const fileName = getFileNameFromContentDisposition(result.contentDisposition, `receipt-${paymentLinkId}.pdf`);
            downloadBlobFile(result.blob, fileName);
        } catch {
            message.error(GetText("AddPaymentLink_ReceiptError"));
        } finally {
            setDownloadingPaymentDocument(false);
        }
    };

    if (loading) {
        return (
            <LayoutLeftMenu selected="users" menu={<LeftMenu selected="users" />}>
                <div style={{ textAlign: 'center', padding: '100px' }}>
                    <Spin size="large" />
                </div>
            </LayoutLeftMenu>
        );
    }

    if (!member) {
        return (
            <LayoutLeftMenu selected="users" menu={<LeftMenu selected="users" />}>
                <Card>
                    <Empty description={GetText("MemberDetails_NotFound")} />
                    <div style={{ textAlign: 'center', marginTop: '20px' }}>
                        <Button
                            type="primary"
                            icon={<ArrowLeftOutlined />}
                            onClick={handleBack}
                        >
                            {GetText("Common_BackToList")}
                        </Button>
                    </div>
                </Card>
            </LayoutLeftMenu>
        );
    }

    return (
        <LayoutLeftMenu fullpageMain={true} selected="users" menu={<LeftMenu selected="users" />}>
            <Col style={{ marginLeft: "20px", minHeight: "450px" }} span={19}>
                <Row gutter={16}>
                    {/* Main Content - Left Side */}
                    <Col xs={24} lg={16}>
                        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                            {/* Header */}
                            <Row justify="space-between" align="middle">
                                <Col>
                                    <Button
                                        icon={<ArrowLeftOutlined />}
                                        onClick={handleBack}
                                    >
                                        {GetText("Common_Back")}
                                    </Button>
                                </Col>
                            </Row>

                            {/* Member Name with Actions */}
                            <Row justify="space-between" align="middle">
                                <Col>
                                    <h2 style={{ margin: 0 }}>
                                        <UserOutlined /> {member.firstName} {member.lastName}
                                    </h2>
                                </Col>
                                <Col>
                                    <Space>
                                        <MemberEditModal
                                            user={user!}
                                            membershipProducts={membershipProducts}
                                            memberFieldSettings={memberFieldSettings}
                                            loadMembers={loadMemberDetails}
                                            members={[member]}
                                            groups={groups}
                                            member={member}
                                            hasPingPaymentIntegration={hasPingPaymentIntegration}
                                        />
                                        <ManagePayments member={member} membershipProducts={membershipProducts} loadMembers={loadMemberDetails} hasPingPaymentIntegration={hasPingPaymentIntegration} hasFortnoxIntegration={hasFortnoxIntegration} hasYearlyPayments={user?.paymentConfiguration === PaymentConfiguration.YearlyPayments} />
                                    </Space>
                                </Col>
                            </Row>

                            {/* Contact, Address and Custom Fields - Merged Card */}
                            <Card size="small">
                                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                                    {/* Contact Information Section */}
                                    <div>
                                        <h3 style={{ marginBottom: '12px', marginTop: 0 }}><MailOutlined /> {GetText("MemberDetails_ContactInfo")}</h3>
                                        <hr style={{ marginBottom: '12px', border: 'none', borderTop: '1px solid #f0f0f0' }} />
                                        <Space direction="vertical" style={{ width: '100%' }}>
                                            {member.email && (
                                                <div><strong>{GetText("MemberList_EmailColumn")}:</strong> <a href={`mailto:${member.email}`}>{member.email}</a></div>
                                            )}
                                            {member.email2 && (
                                                <div><strong>{GetText("MemberList_Email2Column")}:</strong> <a href={`mailto:${member.email2}`}>{member.email2}</a></div>
                                            )}
                                            {member.phoneNumber && (
                                                <div><strong>{GetText("MemberList_PhoneNumberColumn")}:</strong> <a href={`tel:${member.phoneNumber}`}>{member.phoneNumberFormatted || member.phoneNumber}</a></div>
                                            )}
                                            {member.phoneNumber2 && (
                                                <div><strong>{GetText("MemberList_PhoneNumber2Column")}:</strong> <a href={`tel:${member.phoneNumber2}`}>{member.phoneNumber2Formatted || member.phoneNumber2}</a></div>
                                            )}
                                            {user?.companyType === CompanyTypeConfiguration.CRM && member.company && (
                                                <div><strong>{GetText("MemberList_CompanyColumn")}:</strong> {member.company}</div>
                                            )}
                                            {member.webPage && (
                                                <div><strong><GlobalOutlined /> {GetText("MemberList_WebPageColumn")}:</strong> <a href={member.webPage} target="_blank" rel="noopener noreferrer">{member.webPage}</a></div>
                                            )}
                                            <div>
                                                <strong>{GetText("MemberList_EmailNotificationPreferenceColumn")}:</strong>{' '}
                                                {member.emailNotificationPreference === 1
                                                    ? GetText("MemberList_NoEmailNotification")
                                                    : GetText("MemberList_AcceptsEmailNotification")}
                                            </div>
                                        </Space>
                                    </div>

                                    {/* Address Information Section */}
                                    {(member.address || member.postalCode || member.postalArea || member.country) && (
                                        <div>
                                            <h3 style={{ marginBottom: '12px' }}><HomeOutlined /> {GetText("MemberDetails_AddressInfo")}</h3>
                                            <hr style={{ marginBottom: '12px', border: 'none', borderTop: '1px solid #f0f0f0' }} />
                                            <Space direction="vertical" style={{ width: '100%' }}>
                                                {member.address && <div>{member.address}</div>}
                                                {member.adressRow2 && <div>{member.adressRow2}</div>}
                                                {member.adressRow3 && <div>{member.adressRow3}</div>}
                                                {(member.postalCode || member.postalArea) && (
                                                    <div>{member.postalCode} {member.postalArea}</div>
                                                )}
                                                {member.country && <div>{member.country}</div>}
                                            </Space>
                                        </div>
                                    )}

                                    {/* Custom Fields Section */}
                                    {member.customFieldValues && member.customFieldValues.length > 0 && (
                                        <div>
                                            <h3 style={{ marginBottom: '12px' }}>{GetText("MemberDetails_TabCustomFields")}</h3>
                                            <hr style={{ marginBottom: '12px', border: 'none', borderTop: '1px solid #f0f0f0' }} />
                                            <Space direction="vertical" style={{ width: '100%' }}>
                                                {member.customFieldValues.map((field, index) => {
                                                    const fieldSetting = memberFieldSettings.find(f => f.id === field.fieldId);
                                                    return (
                                                        <div key={index}>
                                                            <strong>{fieldSetting?.nameAdmin || fieldSetting?.namePublic || `Field ${index + 1}`}:</strong>{' '}
                                                            {(() => {
                                                                const value = field.fieldValue?.toString();
                                                                if (!value) return '-';
                                                                if (fieldSetting?.uiControl === UiControlType.Date) {
                                                                    const parsed = dayjs(value);
                                                                    if (parsed.isValid()) return parsed.format('YYYY-MM-DD');
                                                                }
                                                                return value;
                                                            })()}
                                                        </div>
                                                    );
                                                })}
                                            </Space>
                                        </div>
                                    )}
                                </Space>
                            </Card>

                            {/* Groups */}
                            {member.groupNames && member.groupNames.length > 0 && (
                                <Card size="small" title={<><TeamOutlined /> {GetText("MemberList_GroupsColumn")}</>}>
                                    <Space wrap>
                                        {member.groupNames.map(name => <Tag key={name} color="blue">{name}</Tag>)}
                                    </Space>
                                </Card>
                            )}

                            {/* Membership Information */}
                            {((member.membershipProduct != null && member.membershipProduct > 0) || (member.membershipProductAddOn && member.membershipProductAddOn.length > 0) || isValidMemberDate(member.joinedDate)) && (
                                <Card size="small" title={<><TeamOutlined /> {GetText("MemberDetails_MembershipInfo")}</>}>
                                    <Space direction="vertical" style={{ width: '100%' }}>
                                        {member.membershipProduct != null && member.membershipProduct > 0 && (
                                            <div>
                                                <div style={{ marginTop: '4px' }}>
                                                    <Tag color="green">{getMembershipProductName(member.membershipProduct)}</Tag>
                                                </div>
                                            </div>
                                        )}
                                        {member.membershipProductAddOn && member.membershipProductAddOn.length > 0 && (
                                            <div>
                                                <div style={{ marginTop: '4px' }}>
                                                    <Space wrap>
                                                        {member.membershipProductAddOn.map(id => {
                                                            const product = membershipProducts.find(p => p.id === id);
                                                            return product ? <Tag key={id} color="cyan">{product.name}</Tag> : null;
                                                        })}
                                                    </Space>
                                                </div>
                                            </div>
                                        )}
                                        {isValidMemberDate(member.joinedDate) && (
                                            <div><strong><CalendarOutlined /> {GetText("MemberList_JoinedDateColumn")}:</strong> {formatDateString(member.joinedDate)}</div>
                                        )}
                                    </Space>
                                </Card>
                            )}
                        </Space>

                        {/* Custom List Connections */}
                        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                            <MemberCustomListConnections
                                member={member}
                                loadMember={loadMemberDetails}
                            />
                        </Space>
                    </Col>

                    {/* Right Sidebar */}
                    <Col xs={24} lg={8}>
                        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                            {/* Member Info */}
                            <Card size="small" title={GetText("MemberDetails_MemberInfo")}>
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    <div><strong>{GetText("MemberList_UniqueIdColumn")}:</strong> {member.uniqueId || '-'}</div>
                                    <div><strong>{GetText("MemberList_StatusColumn")}:</strong> {getStatusTag(member.status)}</div>
                                    {member.title && (
                                        <div><strong>{GetText("MemberList_TitleColumn")}:</strong> {member.title}</div>
                                    )}
                                    {member.personalNumber && (
                                        <div><strong>{GetText("MemberList_PersonalNumberColumn")}:</strong> {member.personalNumber}</div>
                                    )}
                                </Space>
                            </Card>

                            {/* Payment Status */}
                            <Card size="small" title={<><DollarOutlined /> {GetText("MemberDetails_PaymentInfo")}</>}>
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    <div>
                                        <strong>{GetText("MemberList_PaymentCurrentYear")}:</strong> {getPaymentStatusTag(member.isPaidCurrentYear)}
                                    </div>
                                    <div>
                                        <strong>{GetText("MemberList_PaymentLastYear")}:</strong> {getPaymentStatusTag(member.isPaidLastYear)}
                                    </div>
                                    {member.paymentLink && (
                                        <div>
                                            <strong>{GetText("MemberList_PaymentLinkColumn")}:</strong>
                                            <div style={{ marginTop: '4px' }}>
                                                <a href={member.paymentLink} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all' }}>
                                                    {member.paymentLink}
                                                </a>
                                            </div>
                                        </div>
                                    )}
                                    {member.paymentLinkId && member.invoiceUrl && (
                                        <div>
                                            <Button
                                                type="link"
                                                icon={<FilePdfOutlined />}
                                                loading={downloadingPaymentDocument}
                                                onClick={() => downloadInvoiceFromPaymentLink(member.paymentLinkId!)}
                                                style={{ padding: 0 }}
                                            >
                                                {GetText("AddPaymentLink_DownloadInvoice")}
                                            </Button>
                                        </div>
                                    )}
                                    {member.paymentLinkId && member.isPaidCurrentYear && (
                                        <div>
                                            <Button
                                                type="link"
                                                icon={<FilePdfOutlined />}
                                                loading={downloadingPaymentDocument}
                                                onClick={() => downloadReceiptFromPaymentLink(member.paymentLinkId!)}
                                                style={{ padding: 0 }}
                                            >
                                                {GetText("AddPaymentLink_DownloadReceipt")}
                                            </Button>
                                        </div>
                                    )}
                                </Space>
                            </Card>

                            {/* Payment Documents */}
                            {(hasFortnoxIntegration || hasSpirisIntegration) && <MemberPaymentDocuments member={member} />}

                            {/* System Info */}
                            <Card size="small" title={GetText("MemberDetails_SystemInfo")}>
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    {member.createdAt && (
                                        <div><strong><CalendarOutlined /> {GetText("MemberList_CreatedColumn")}:</strong> {formatDateString(member.createdAt)}</div>
                                    )}
                                    {member.lastModified && (
                                        <div><strong><CalendarOutlined /> {GetText("MemberList_LastModifiedColumn")}:</strong> {formatDateString(member.lastModified)}</div>
                                    )}
                                </Space>
                            </Card>

                            {/* Extra Fields */}
                            {(member.extra1 || member.extra2 || member.extra3) && (
                                <Card size="small" title={GetText("MemberDetails_AdditionalInfo")}>
                                    <Space direction="vertical" style={{ width: '100%' }}>
                                        {member.extra1 && (
                                            <div><strong>{GetText("MemberList_Extra1Column")}:</strong> {member.extra1}</div>
                                        )}
                                        {member.extra2 && (
                                            <div><strong>{GetText("MemberList_Extra2Column")}:</strong> {member.extra2}</div>
                                        )}
                                        {member.extra3 && (
                                            <div><strong>{GetText("MemberList_Extra3Column")}:</strong> {member.extra3}</div>
                                        )}
                                    </Space>
                                </Card>
                            )}
                        </Space>
                    </Col>
                </Row>
            </Col>
        </LayoutLeftMenu>
    );
};

export default MemberDetails;
